import { Link } from "react-router-dom";
import { useLanguage } from "../context/LanguageContext";
import useSiteText from "../hooks/useSiteText";
import OptimizedImage from "../components/OptimizedImage";
import { Flame, Droplets, Recycle, Heart, ChevronRight, Leaf, Shield } from "lucide-react";

const SustainabilityPage = () => {
  const { t } = useLanguage();
  const text = useSiteText();

  const features = [
    { icon: Flame, ...t.sustainability.features[0] },
    { icon: Droplets, ...t.sustainability.features[1] },
    { icon: Recycle, ...t.sustainability.features[2] },
    { icon: Heart, ...t.sustainability.features[3] }
  ];

  return (
    <div className="pt-20" data-testid="sustainability-page">
      {/* Header */}
      <section className="py-24 bg-emerald-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3 mb-6">
            <Leaf className="w-8 h-8 text-emerald-600" />
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-600">{t.sustainability.overline}</p>
          </div>
          <h1 className="text-5xl sm:text-6xl font-black tracking-tighter text-[#0A0A0A] mb-6" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
            {t.sustainability.title}
          </h1>
          <p className="text-xl text-zinc-600 max-w-2xl">{t.sustainability.subtitle}</p>
        </div>
      </section>

      {/* Main Features */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12">
            {features.map((feature, index) => (
              <div key={index} className="flex gap-6 p-8 bg-zinc-50 border border-zinc-100">
                <div className="w-16 h-16 bg-emerald-100 flex items-center justify-center flex-shrink-0">
                  <feature.icon className="w-8 h-8 text-emerald-600" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-[#0A0A0A] mb-3" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                    {feature.title}
                  </h3>
                  <p className="text-zinc-600 leading-relaxed">{feature.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* EU Standards */}
      <section className="py-24 bg-[#0A0A0A] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="flex items-center gap-3 mb-6">
                <Shield className="w-8 h-8 text-emerald-400" />
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-400">{t.sustainability.euOverline}</p>
              </div>
              <h2 className="text-4xl sm:text-5xl font-bold tracking-tight mb-6" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                {t.sustainability.closing}
              </h2>
              <p className="text-zinc-400 text-lg mb-8">
                {text("sustainability.eu_desc", t.sustainability.euDesc)}
              </p>
            </div>
            <div className="relative h-[400px]">
              <OptimizedImage
                src="https://meat-chain-pro.preview.emergentagent.com/api/uploads/5ed5062d28424bf1abe86111b2d7c3f9.webp"
                alt="EU Standards"
                sizes="(max-width: 1024px) 100vw, 50vw"
                className="w-full h-full object-cover grayscale"
              />
              <div className="absolute inset-0 bg-emerald-900/20"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Closing Statement */}
      <section className="py-24 bg-emerald-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Leaf className="w-12 h-12 text-emerald-600 mx-auto mb-6" />
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-[#0A0A0A] mb-6" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
            {text("sustainability.future_title", t.sustainability.futureTitle)}
          </h2>
          <p className="text-zinc-600 text-lg mb-8 max-w-2xl mx-auto">
            {text("sustainability.future_desc", t.sustainability.futureDesc)}
          </p>
          <Link 
            to="/contact" 
            className="inline-flex items-center gap-2 bg-[#0A0A0A] text-white px-8 py-4 font-medium hover:bg-zinc-800 transition-colors"
          >
            {t.hero.cta} <ChevronRight className="w-5 h-5" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default SustainabilityPage;
